import { Fragment, memo, useMemo } from 'react'
import katex from 'katex'
import { normalizeKatexMath } from '../../utils/KatexRenderer'

const MATH_PATTERN = /\$\$([^$]+)\$\$/g

interface MetadataPart {
  text: string
  math?: string
}

function splitMetadataText(text: string): MetadataPart[] {
  const pattern = new RegExp(MATH_PATTERN)
  const parts: MetadataPart[] = []
  let cursor = 0
  let match: RegExpExecArray | null
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > cursor) parts.push({ text: text.slice(cursor, match.index) })
    parts.push({ text: match[0], math: match[1] })
    cursor = pattern.lastIndex
  }
  if (cursor < text.length) parts.push({ text: text.slice(cursor) })
  return parts
}

export function hasMetadataMath(text: string | null | undefined): boolean {
  return typeof text === 'string' && new RegExp(MATH_PATTERN).test(text)
}

/** Concepts remain strings; only commas outside complete math delimiters separate items. */
export function splitMetadataConcepts(value: string | null | undefined): string[] {
  if (!value) return []
  const concepts: string[] = []
  let current = ''
  for (const part of splitMetadataText(value)) {
    if (part.math !== undefined) {
      current += part.text
      continue
    }
    const fragments = part.text.split(',')
    fragments.forEach((fragment, index) => {
      current += fragment
      if (index < fragments.length - 1) {
        if (current.trim()) concepts.push(current.trim())
        current = ''
      }
    })
  }
  if (current.trim()) concepts.push(current.trim())
  return concepts
}

const MetadataMath = memo(function MetadataMath({ math, raw }: { math: string; raw: string }) {
  const markup = useMemo(() => {
    try {
      return katex.renderToString(normalizeKatexMath(math), {
        throwOnError: false,
        displayMode: false,
      })
    } catch {
      return null
    }
  }, [math])
  return markup == null ? <>{raw}</> : (
    <span
      className="content-metadata-math"
      style={{ display: 'inline-block', verticalAlign: 'baseline', whiteSpace: 'nowrap', lineHeight: 'normal' }}
      dangerouslySetInnerHTML={{ __html: markup }}
    />
  )
})

export interface ContentMetadataTextProps {
  text: string | null | undefined
  className?: string
}

/** Metadata is not Markdown or HTML. Ordinary response text is rendered unchanged. */
export function ContentMetadataText({ text, className }: ContentMetadataTextProps) {
  const parts = useMemo(() => splitMetadataText(text ?? ''), [text])
  if (!text) return null
  const content = parts.map((part, index) => part.math !== undefined ? (
    <MetadataMath key={index} math={part.math} raw={part.text} />
  ) : <Fragment key={index}>{part.text}</Fragment>)
  // A title remains one flex item even when it contains text on both sides of math.
  return className || parts.some(part => part.math !== undefined)
    ? <span className={className}>{content}</span>
    : <>{content}</>
}
